import { Paperclip, Receipt } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { Field, FormError, SelectField, TextareaField } from '@/components/ui/field';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  EXPENSE_TONES,
  useMyExpenses,
  useSubmitMyExpense,
  useUploadMyReceipt,
} from '@/hooks/expenses';
import { ApiError } from '@/lib/api';
import { formatDay } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { formatCents, parseMoneyInput } from '@shared/money';
import {
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_CATEGORY_VALUES,
  EXPENSE_STATUS_LABELS,
  type ExpenseStatusValue,
  type ExpenseView,
} from '@shared/schemas/expense';
import { DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * /employee/expenses — ROADMAP.md §10.1, "employees submit their own".
 *
 * **This is a phone screen.** Cards rather than a table, one column, and every
 * control at least 44×44 — P23 and P24, which is the prototype's "Add photo"
 * button at 99×30 being tapped ten times a job. The form is above the list
 * because submitting is why somebody opens this, and reading back is secondary.
 *
 * **No pay rate and no total earnings anywhere on this screen.** A cleaner's own
 * hourly rate is deliberately absent from the employee portal — stricter than
 * business-rules.md §2 requires, decided out loud rather than inherited, and
 * pinned by a test. An expense is money going the other way and shows its own
 * amount; nothing here derives what anybody is paid.
 */

const STATUS_FILTERS: { value: ExpenseStatusValue | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: EXPENSE_STATUS_LABELS.pending },
  { value: 'approved', label: EXPENSE_STATUS_LABELS.approved },
  { value: 'declined', label: EXPENSE_STATUS_LABELS.declined },
];

export function EmployeeExpensesPage() {
  const toast = useToast();
  const [status, setStatus] = useState<ExpenseStatusValue | 'all'>('all');
  const [page, setPage] = useState(1);

  const expenses = useMyExpenses({ page, status });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6">
      <header className="mb-6">
        <h1 className="text-h1 text-ink-900">My Expenses</h1>
        <p className="mt-1 text-body text-ink-500">
          Anything you have paid for out of pocket. The office approves them.
        </p>
      </header>

      <SubmitExpenseForm />

      <h2 className="mb-3 mt-8 text-h2 text-ink-900">What you have submitted</h2>

      <div className="mb-4">
        <FilterPills
          label="Filter my expenses by status"
          options={STATUS_FILTERS}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />
      </div>

      {expenses.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          {/* A failed request is an error, never an empty state — P22. */}
          <ErrorState
            onRetry={() => {
              void expenses.refetch();
            }}
          />
        </div>
      ) : expenses.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading your expenses</span>
          <div aria-hidden="true" className="h-40 animate-pulse rounded-lg bg-ink-100" />
        </div>
      ) : expenses.data && expenses.data.data.length > 0 ? (
        <ul className="space-y-3">
          {expenses.data.data.map((entry) => (
            <ExpenseCard
              key={entry.id}
              expense={entry}
              onAttached={() => {
                toast.success('Receipt attached.');
              }}
            />
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Receipt}
            title="You have not submitted any expenses."
            hint="Fill in the form above when you have paid for something out of pocket."
          />
        </div>
      )}

      <Pagination
        page={page}
        pageSize={expenses.data?.pageSize ?? DEFAULT_PAGE_SIZE}
        total={expenses.data?.total ?? 0}
        onPageChange={setPage}
        itemNoun="expenses"
      />
    </div>
  );
}

function SubmitExpenseForm() {
  const toast = useToast();
  const submit = useSubmitMyExpense();

  const [category, setCategory] = useState<string>('supplies');
  const [amount, setAmount] = useState('');
  const [incurredOn, setIncurredOn] = useState('');
  const [description, setDescription] = useState('');
  const [amountError, setAmountError] = useState<string | undefined>(undefined);

  const apiError = submit.error instanceof ApiError ? submit.error : null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    // Text to integer cents, once. Nothing past this line is a decimal.
    const parsed = parseMoneyInput(amount);
    if (!parsed.ok) {
      setAmountError(parsed.message);
      return;
    }
    if (parsed.cents === null) {
      setAmountError('Enter an amount');
      return;
    }
    setAmountError(undefined);

    submit.mutate(
      {
        category: category as (typeof EXPENSE_CATEGORY_VALUES)[number],
        amountCents: parsed.cents,
        incurredOn,
        description: description.trim() === '' ? null : description.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Submitted. The office will review it.');
          setAmount('');
          setDescription('');
        },
      },
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-lg border border-ink-300 bg-white p-4 shadow-card"
    >
      <h2 className="text-h2 text-ink-900">Submit an expense</h2>

      <SelectField
        label="What kind"
        required
        value={category}
        onChange={(event) => {
          setCategory(event.target.value);
        }}
        options={EXPENSE_CATEGORY_VALUES.map((value) => ({
          value,
          label: EXPENSE_CATEGORY_LABELS[value],
        }))}
      />

      <Field
        label="How much"
        required
        // `decimal` rather than `numeric`, so a phone shows a keypad with a
        // decimal point on it.
        inputMode="decimal"
        placeholder="12.50"
        value={amount}
        error={amountError}
        onChange={(event) => {
          setAmount(event.target.value);
        }}
      />

      <Field
        label="When you paid"
        required
        type="date"
        value={incurredOn}
        onChange={(event) => {
          setIncurredOn(event.target.value);
        }}
      />

      <TextareaField
        label="What it was for"
        rows={2}
        value={description}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
      />

      {apiError ? <FormError>{apiError.message}</FormError> : null}

      <Button type="submit" className="w-full" disabled={submit.isPending}>
        {submit.isPending ? 'Submitting…' : 'Submit it'}
      </Button>

      <p className="text-meta text-ink-500">
        Add the receipt after it is submitted — the card below it has a button for the photo.
      </p>
    </form>
  );
}

function ExpenseCard({ expense, onAttached }: { expense: ExpenseView; onAttached: () => void }) {
  const upload = useUploadMyReceipt();
  const apiError = upload.error instanceof ApiError ? upload.error : null;

  function attach(file: File) {
    const form = new FormData();
    form.set('receipt', file);
    upload.mutate({ id: expense.id, form }, { onSuccess: onAttached });
  }

  return (
    <li className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-h3 tabular-nums text-ink-900">{formatCents(expense.amountCents)}</p>
          <p className="text-body text-ink-500">
            {expense.categoryLabel} · {formatDay(expense.incurredOn)}
          </p>
        </div>
        <StatusBadge label={expense.statusLabel} tone={EXPENSE_TONES[expense.status]} />
      </div>

      {expense.description ? (
        <p className="mt-2 text-body text-ink-900">{expense.description}</p>
      ) : null}

      {expense.reviewNote ? (
        <p className="mt-2 rounded-sm bg-honey-50 p-2 text-body text-ink-900">
          <span className="font-medium">From the office: </span>
          {expense.reviewNote}
        </p>
      ) : null}

      <div className="mt-3">
        {expense.hasReceipt ? (
          <a
            href={`/api/employee/expenses/${expense.id}/receipt`}
            className="inline-flex min-h-touch items-center gap-1 text-body text-bark-700 underline"
          >
            <Paperclip aria-hidden="true" className="size-4" />
            {expense.receiptFilename ?? 'Receipt'}
          </a>
        ) : (
          <>
            {/*
              A file input styled as a target big enough to hit on a phone.
              `capture` is deliberately absent — a cleaner may already have
              photographed the receipt, and forcing the camera would stop them
              picking it out of their gallery.

              **`focus-within` is load-bearing, and it was missing.** The input
              is `sr-only`, which means `clip: rect(0,0,0,0)` — so the global
              `:focus-visible` ring in styles/index.css *is* applied to it and
              *is* drawn on a box clipped to nothing. Measured rather than read:
              the input reported `outline: 2px solid` while the label a keyboard
              user is actually looking at reported `outline-style: none`. So
              tabbing to the control that attaches a receipt to a reimbursement
              claim showed nothing at all.

              This is 13.3's finding in a new place — that one was two controls
              carrying `focus:outline-none`, found by measuring computed styles
              rather than by reading the JSX, which looked fine. This looked fine
              too.
            */}
            <label className="inline-flex min-h-touch cursor-pointer items-center gap-2 rounded-sm border border-ink-300 px-3 py-2 text-body text-ink-900 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink-900">
              <Paperclip aria-hidden="true" className="size-4" />
              {upload.isPending ? 'Uploading…' : 'Add the receipt'}
              <input
                type="file"
                accept="application/pdf,image/*"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) attach(file);
                }}
              />
            </label>
            {apiError ? (
              <p className="mt-2">
                <FormError>{apiError.message}</FormError>
              </p>
            ) : null}
          </>
        )}
      </div>
    </li>
  );
}
